package macha.sh.kotlin

import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.components.layouts.SiteContentMaxWidth
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.alignSelf
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.compose.css.functions.clamp
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw

fun hex(c: String): Color {
    val v = c.removePrefix("#").toInt(16)
    return Color.rgb(v)
}

object SiteColors {
    object Main {
        val SiteBkg: Color = hex("#0d0d0d")
        val SiteText: Color = hex("#a6a6a6")
        val BoxBkg: Color = hex("#0d0d0d")
        val Border: Color = hex("#008000")
        val Hover: Color = hex("#49d049")
    }
    object BannerGlitch {
        val Base = "#f8f9ff"
        val Alt = "#49d049"
        val ColorA = "#ff2d95"
        val ColorB = "#00f0ff"
        val ColorC = "#ffd319"
    }
    object Heatmap {
        val None = "#000000ff"
        val Low = "#223b4d"
        val Mid = "#0d6f8f"
        val High = "#32b5e4"
        val Max = "#8be9ff"
        val Border = "#03b52dff"
    }
    object Text {
        val Default: Color = hex("#a6a6a6")
        val Gray: Color = hex("#323232")
    }
    val SiteBkg get() = Main.SiteBkg
    val SiteText get() = Main.SiteText
    val BoxBkg get() = Main.BoxBkg
    val Border get() = Main.Border
    val Hover get() = Main.Hover
}

object SiteModifiers {
    val PrimaryPanel: Modifier = Modifier
        .fillMaxWidth()
        .border(width = 3.px, style = LineStyle.Solid, color = SiteColors.Border)
        .backgroundColor(SiteColors.BoxBkg)
    val Panel: Modifier = Modifier
        .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
        .backgroundColor(SiteColors.BoxBkg)

    object Navi {
        fun banner(): Modifier = Modifier
            .fillMaxWidth()
            .maxWidth(SiteContentMaxWidth)
            .border(width = 3.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(topBottom = 0.5.cssRem)
            .backgroundColor(SiteColors.BoxBkg)
            .color(SiteColors.SiteText)
            .applyFont(SiteTypography.Banner)
            .textAlign(TextAlign.Center)
            .fontSize(clamp(2.25.cssRem, 10.vw, 7.5.cssRem))
        fun naviContainer(): Modifier = Modifier
            .fillMaxWidth()
            .maxWidth(SiteContentMaxWidth)
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 3.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(1.cssRem)
            .boxShadow(blurRadius = 0.5.cssRem, color = Colors.Black)
        fun navi(): Modifier = Modifier
            .fillMaxWidth()
            .gap(1.5.cssRem)
            .justifyContent(JustifyContent.Center)
            .alignItems(AlignItems.Center)
        fun naviItems(): Modifier = Modifier
            .gap(1.5.cssRem)
            .applyFont(SiteTypography.Subheading, fontSize = 1.75.cssRem)
            .displayIfAtLeast(Breakpoint.MD)
        fun hamburger(): Modifier = Modifier
            .fontSize(1.5.cssRem)
            .gap(1.cssRem)
            .displayUntil(Breakpoint.MD)
    }
    object Home {
        private val AvatarWidth = 8.cssRem
        fun mainRow(): Modifier = Modifier
            .fillMaxWidth()
            .textAlign(TextAlign.Center)
            .alignItems(AlignItems.Stretch)
            .displayIfAtLeast(Breakpoint.MD)
        fun mobileColumn(): Modifier = Modifier
            .fillMaxWidth()
            .displayUntil(Breakpoint.MD)
        fun mobileTopRow(): Modifier = Modifier
            .fillMaxWidth()
            .alignItems(AlignItems.Stretch)
        fun mobileHeatmap(): Modifier = Modifier
            .alignSelf(AlignSelf.Stretch)
            .minWidth(0.px)
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .flexGrow(1.0)
            .flexShrink(1.0)
            .padding(0.5.cssRem)
        fun mobileGitHistory(): Modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(0.75.cssRem)
        fun pfp(): Modifier = Modifier
            .width(AvatarWidth)
            .flexShrink(0) // Don't let avatar box shrink on narrow widths
            .alignSelf(AlignSelf.Stretch)
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
        fun heatmap(): Modifier = Modifier
            .alignSelf(AlignSelf.Stretch)
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .flexGrow(1.0)
            .flexShrink(0) // Keep heatmap from shrinking; history panel will absorb shrink
            .padding(1.cssRem)
        fun gitHistory(): Modifier = Modifier
            .alignSelf(AlignSelf.Stretch)
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .flexGrow(9.0)
            .padding(1.cssRem)
        fun ascii(): Modifier = Modifier
            .fillMaxWidth()
            //.border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .backgroundColor(SiteColors.BoxBkg)
            .padding(1.cssRem)
        fun asciiBorder(): Modifier = Modifier 
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .borderRadius(1.cssRem)
            .backgroundColor(SiteColors.BoxBkg)
            .fillMaxWidth()
            .maxWidth(100.percent)
        fun welcomeBox(): Modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(SiteColors.BoxBkg)
            .padding(1.cssRem)
    }
    object Blog {
        fun mainColumn(): Modifier = Modifier
            .fillMaxWidth()
            .textAlign(TextAlign.Center)
        fun feedPanel(): Modifier = Panel
            .fillMaxWidth()
            .padding(1.cssRem)
        fun row(): Modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.Red)
        fun subMenu(): Modifier = Panel
            .width(25.percent)
        fun subMenuColumn(): Modifier = Modifier
            .fillMaxWidth()
            .textAlign(TextAlign.End)
            .alignItems(AlignItems.End)
            .backgroundColor(SiteColors.BoxBkg)
        fun subMenuItem(): Modifier = Panel
            .fillMaxWidth()
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .justifyContent(JustifyContent.End)
            .padding(0.5.cssRem)
        fun subMenuPrefix(): Modifier = Modifier
            .applyFont(SiteTypography.BodyL, fontSize = 1.5.cssRem)
            .color(SiteColors.Text.Gray)
        fun subMenuText(): Modifier = Modifier
            .applyFont(SiteTypography.Orbitron, fontSize = 1.25.cssRem)
            .color(SiteColors.Text.Default)
        fun contentArea(): Modifier = Panel
            .alignSelf(AlignSelf.Stretch)
            .width(75.percent)
            .padding(1.cssRem)
        fun articleContainer(): Modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
        fun articleInfoBox(): Modifier = Modifier
            .width(75.percent)
            .backgroundColor(SiteColors.BoxBkg)
            //.border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
        fun articleImgBox(): Modifier = Modifier
            .width(25.percent)
            .alignSelf(AlignSelf.Stretch)
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(1.cssRem)
        fun titleText(): Modifier = Modifier
            .applyFont(SiteTypography.BodyL, fontSize = 1.35.cssRem)
            .color(SiteColors.SiteText)
            .padding(0.5.cssRem)
        fun descText(): Modifier = Modifier
            .applyFont(SiteTypography.BodyM, fontSize = 1.cssRem)
            .color(SiteColors.SiteText)
            .padding(0.5.cssRem)
    }
    object About {
        fun container(): Modifier = Modifier
            .fillMaxWidth()
            .padding(1.5.cssRem)
            .border(width = 3.px, style = LineStyle.Solid, color = SiteColors.Border)
            .backgroundColor(SiteColors.BoxBkg)
        fun text(): Modifier = Modifier
            .fillMaxWidth()
            .applyFont(SiteTypography.Body)
            .fontSize(1.0.cssRem)
            .lineHeight(1.5)
            .color(SiteColors.SiteText)
    }
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.background = SiteColors.SiteBkg
    ctx.theme.palettes.light.color = SiteColors.SiteText
    ctx.theme.palettes.dark.background = SiteColors.SiteBkg
    ctx.theme.palettes.dark.color = SiteColors.SiteText
}
